import moment from "moment";
import { stripNumbersFromUsername } from "_helpers";
import { msgSource } from "_constants";

const CHANNEL_GROUPS = [
  { label: "Shopify", aliases: ["shopify"] },
  { label: "Gorgias", aliases: ["gorgias"] },
  { label: "Zendesk", aliases: ["zendesk"] },
  { label: "Dixa", aliases: ["dixa"] },
  { label: "TikTok", aliases: ["tiktok"] },
  { label: "Facebook", aliases: ["facebook"] },
  { label: "Instagram", aliases: ["instagram"] },
  { label: "YouTube", aliases: ["youtube"] },
  { label: "Salesforce", aliases: ["salesforce", "service cloud"] },
  {
    label: "Customer.io",
    aliases: ["customer.io", "customerio", "customer io"],
  },
  { label: "Freshdesk", aliases: ["freshdesk"] },
  { label: "Front", aliases: ["frontapp", "front"] },
  { label: "Trustpilot", aliases: ["trustpilot"] },
  { label: "Reamaze", aliases: ["reamaze"] },
  { label: "Kustomer", aliases: ["kustomer"] },
  { label: "Help Scout", aliases: ["help scout", "helpscout"] },
  { label: "Gladly", aliases: ["gladly"] },
];

const OTHER_CHANNEL_GROUP = "Other";
const AUTHOR_ID_PATTERN = /^.+\(#?([^()#]+)\)$/;

const channelTrendsId = (channel) => {
  const id = channel?.trendsId;
  if (!id) return null;
  return String(id);
};

const channelGroupLabel = (name) => {
  const normalized = name.toString().toLowerCase();
  const match = CHANNEL_GROUPS.find((group) =>
    group.aliases.some((alias) => nameIncludesAlias(normalized, alias)),
  );
  return match ? match.label : OTHER_CHANNEL_GROUP;
};

const nameIncludesAlias = (name, alias) => {
  const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(?:^|[^a-z0-9])${escaped}`, "i").test(name);
};

export const INBOX_STATUS_OPTIONS = [
  { value: "OPEN", label: "Open" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "NO_RESPONSE_NEEDED", label: "No response needed" },
  { value: "HIDDEN", label: "Hidden" },
];

export const SENTIMENT_OPTIONS = [
  { value: "NEGATIVE", label: "Negative", color: "#dc4a3d" },
  { value: "NEUTRAL", label: "Neutral", color: "#c79a4e" },
  { value: "POSITIVE", label: "Positive", color: "#3f9f63" },
];

export const ALL_SENTIMENTS = SENTIMENT_OPTIONS.map((option) => option.value);

export const toInboxDateRange = (startDate, endDate) => {
  const start = startDate ? moment(startDate) : moment().subtract(6, "days");
  const end = endDate ? moment(endDate) : moment();

  return {
    startDate: start.clone().startOf("day").toISOString(),
    endDate: end.clone().endOf("day").toISOString(),
  };
};

export const describeRange = (start, end, ranges) => {
  const selectedStart = moment(start);
  const selectedEnd = moment(end);
  const match = ranges.find(
    (item) =>
      selectedStart.isSame(item.range[0], "day") &&
      selectedEnd.isSame(item.range[1], "day"),
  );

  if (match) {
    return match.label;
  }

  return `${selectedStart.format("MM/DD/YYYY")} - ${selectedEnd.format("MM/DD/YYYY")}`;
};

const sortChannels = (channels = []) => {
  if (!channels?.length) return [];
  return channels.sort(function (a, b) {
    if (a.label < b.label) {
      return -1;
    }
    if (a.label > b.label) {
      return 1;
    }
    return 0;
  });
};

export const groupChannels = (channels = []) => {
  const grouped = new Map();
  const channelList = Array.isArray(channels) ? channels : [];

  channelList.forEach((channel) => {
    const id = channelTrendsId(channel);
    if (!id) return;

    const label = channelGroupLabel(channel?.name);
    if (!grouped.has(label)) {
      grouped.set(label, { label, channelIds: [] });
    }

    const group = grouped.get(label);
    if (!group.channelIds.includes(id)) {
      group.channelIds.push(id);
    }
  });

  const available = CHANNEL_GROUPS.map((group) =>
    grouped.get(group.label),
  ).filter(Boolean);

  if (grouped.has(OTHER_CHANNEL_GROUP)) {
    available.push(grouped.get(OTHER_CHANNEL_GROUP));
  }

  return sortChannels(available);
};

export const channelIdsForGroups = (groups = [], selectedLabels = []) => {
  return groups
    .filter((group) => selectedLabels.includes(group.label))
    .flatMap((group) => group.channelIds);
};

export const channelHelperText = (groups, activeLabels) => {
  const activeLabelsLength = activeLabels.length;
  if (!activeLabelsLength) return "None";
  if (activeLabelsLength === groups.length) return "All";
  if (activeLabelsLength <= 2) return activeLabels.join(", ");
  return `${activeLabelsLength} of ${groups.length}`;
};

export const parseInboxStatus = (value) => {
  const status = String(value || "")
    .trim()
    .toUpperCase();
  return INBOX_STATUS_OPTIONS.some((option) => option.value === status)
    ? status
    : null;
};

export const parseSentiments = (value) => {
  if (value == null) return ALL_SENTIMENTS;
  const tokens = String(value)
    .split(",")
    .map((token) => token.trim().toUpperCase())
    .filter(Boolean);
  return ALL_SENTIMENTS.filter((sentiment) => tokens.includes(sentiment));
};

export const toSentimentParam = (selected = []) => {
  if (!selected.length) return "";
  if (selected.length === ALL_SENTIMENTS.length) return null;
  return ALL_SENTIMENTS.filter((sentiment) =>
    selected.includes(sentiment),
  ).join(",");
};

export const getAuthorId = (author) => {
  if (!author) return null;
  const match = String(author).trim().match(AUTHOR_ID_PATTERN);
  return match ? match[1].trim() : null;
};

export const getProfileUrl = (conversation, sourceName) => {
  const author = _.get(conversation, "author");
  const username = stripNumbersFromUsername(author).trim();
  const authorId = getAuthorId(author);
  const source = _.toString(sourceName).toLowerCase();

  switch (source) {
    case msgSource.FACEBOOK:
      return authorId ? `https://www.facebook.com/${authorId}` : null;
    case msgSource.INSTAGRAM:
      return username && !/\s/.test(username)
        ? `https://www.instagram.com/${username}/`
        : null;
    case msgSource.TIKTOK:
    case msgSource.TIKTOK_ADS:
      return username
        ? `https://www.tiktok.com/@${username.replace(/^@/, "")}`
        : null;
    case msgSource.YOUTUBE:
      if (authorId) {
        return `https://www.youtube.com/channel/${authorId}`;
      }
      return username
        ? `https://www.youtube.com/@${username.replace(/^@/, "")}`
        : null;
    default:
      return null;
  }
};

export const sumObj = (obj) => {
  let sum = 0;
  if (obj) {
    for (let el in obj) {
      if (obj.hasOwnProperty(el)) {
        sum += parseFloat(obj[el]);
      }
    }
  }
  return sum;
};
